import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { register } from 'swiper/element/bundle';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import {
  IonApp,
  IonMenu,
  IonRouterOutlet,
  NavController,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import { MenuUserComponent } from './role-user/components/menu-user/menu-user.component';
import { MenuCharityComponent } from './role-charity/components/menu-charity/menu-charity.component';
import { MenuBusinessComponent } from './role-business/components/menu-business/menu-business.component';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthService } from './core/services/auth/auth.service';
import { UserRolesService } from './core/services/auth/user-roles.service';
import { ThemeService } from './core/services/settings/theme.service';
import { WebSocketService } from './core/services/websocket/websocket.service';
import { PwaUpdateService } from './core/services/pwa/pwa-update.service';
import { ChunkErrorHandler } from './core/services/error/chunk-error-handler';
import { markUserNotified } from './core/services/error/app-error-handler';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet, IonMenu, MenuUserComponent, MenuCharityComponent, MenuBusinessComponent],
})
export class AppComponent implements OnInit {
  private readonly subscription: Subscription = new Subscription();
  
  isMenuOpen: boolean = false;
  isCharityMenuOpen: boolean = false;
  isBusinessMenuOpen: boolean = false;
  isUserSide: boolean = true;
  isLoggedIn: boolean = false;

  private readonly titleService = inject(Title);
  private readonly chunkErrorHandler: ChunkErrorHandler = inject(ChunkErrorHandler);
  private readonly toastController: ToastController = inject(ToastController);
  private readonly navController: NavController = inject(NavController);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly userRolesService: UserRolesService,
    private readonly themeService: ThemeService,
    private readonly platform: Platform,
    private readonly webSocketService: WebSocketService,
    private readonly pwaUpdateService: PwaUpdateService
  ) {
    defineCustomElements(window);
    this.setEnvironmentBranding();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe((event: NavigationEnd) => {
      this.updateActiveSide(event.url);
    });

    // Failed navigations reach the router.events stream as NavigationError; without this,
    // a broken route silently does nothing (dead-button feel). Chunk-load NavigationErrors are
    // skipped here — the global ErrorHandler path (AppErrorHandler → ChunkErrorHandler) already
    // owns the reload UX for those (SUN-692 Decision 6).
    this.router.events.pipe(
      filter(event => event instanceof NavigationError),
      takeUntilDestroyed(),
    ).subscribe((event: NavigationError) => {
      this.handleNavigationError(event);
    });
  }

  ngOnInit(): void {
    ChunkErrorHandler.resetRetryCount();
    PwaUpdateService.resetIntegrityRetryCount();
    this.getLoggedInUser();
    
    this.platform.resume.subscribe(() => {
      this.themeService.refreshTheme();
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.themeService.refreshTheme();
      }
    });

    window.addEventListener('focus', () => {
      this.themeService.refreshTheme();
    });
  }

  getLoggedInUser(): void {
    this.subscription.add(
      this.authService.user$.subscribe(user => {
        if (user) {
          this.isLoggedIn = true;
        } else {
          this.isLoggedIn = false;
        }
      }),
    );
  }

  setOpen(isOpen: boolean): void {
    this.isMenuOpen = isOpen;
  }

  setCharityOpen(isOpen: boolean): void {
    this.isCharityMenuOpen = isOpen;
  }

  setBusinessOpen(isOpen: boolean): void {
    this.isBusinessMenuOpen = isOpen;
  }

  private handleNavigationError(event: NavigationError): void {
    if (this.chunkErrorHandler.isChunkLoadError(event.error)) {
      return;
    }

    // Mark before the generic ErrorHandler sees it: RouterLink ignores the navigation promise,
    // so this same error also arrives as an unhandled rejection → AppErrorHandler. Marking here
    // first (NavigationError fires before that rejection is delivered, a later microtask)
    // prevents a duplicate generic toast on top of this specific one (SUN-692 Decision 4).
    markUserNotified(event.error);
    void this.presentNavigationErrorToast();

    if (!this.router.navigated) {
      this.navController.navigateRoot('/');
    }
  }

  private async presentNavigationErrorToast(): Promise<void> {
    try {
      const toast: HTMLIonToastElement = await this.toastController.create({
        message: "Couldn't open that page. Please try again.",
        duration: 4000,
        position: 'top',
        color: 'warning',
      });
      await toast.present();
    } catch {
      // Toast presentation must never crash navigation handling.
    }
  }

  private updateActiveSide(url: string): void {
    if (url.includes('/main')) {
      this.isUserSide = true;
    } else if (url.includes('/charity') || url.includes('/business')) {
      this.isUserSide = false;
    } else {
      this.isUserSide = true;
    }
  }

  private setEnvironmentBranding(): void {
    this.titleService.setTitle(environment.appName);

    const faviconPath: string = this.getFaviconPath();
    const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = faviconPath;
    }
  }

  private getFaviconPath(): string {
    switch (environment.name) {
      case 'local':
        return 'assets/icons/local/72.png';
      case 'development':
        return 'assets/icons/dev/72.png';
      case 'production':
        return 'assets/icons/prod/72.png';
      default:
        return 'assets/sun-logo-clay.svg';
    }
  }
}
